import { useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  emailVerify,
  resetPasswordStoreValues
} from '../../Store/reducers/ForgotPassword';
import { useReduxDispatch, useReduxSelector } from '../../Store/reduxHooks';
import { colorState } from '../../Universal/Foundation';
import { Button, Icon, Input } from '../../Universal/NovusDSImports';
import {
  btnStyles,
  inputStyles
} from '../../Universal/NovusDSImports/variants';
import { ErrorMessage, FlexCenter, FormContainer } from './styles';
import { STRINGS } from '../../Constants/ConstantStrings';
import { REACPTCHA_SITE_KEY } from '../../settings';
import { RootState } from '../../store';
import ErrorBoundary from '../../GenericErrorBoundary';
interface ForgotPasswordPropType {
  goBack: () => void;
}

export default function ForgotPassword(props: ForgotPasswordPropType) {
  const dispatch = useReduxDispatch(),
    emailVerified = useSelector(
      (state: RootState) => state.ForgotPassword.emailVerified
    ),
    message = useSelector(
      (state: RootState) => state.ForgotPassword.onPasswordUpdateMessage
    ),
    errorCode = useSelector(
      (state: RootState) => state.ForgotPassword.errorCode
    ),
    captchaEnabled = useSelector(
      (state: RootState) => state.ClientInfo.captcha_enabled
    );

  const ForgotPasswordSchema = yup.object().shape({
    email: yup
      .string()
      .email(STRINGS.YUP.INVALID_EMAIL_ADDRESS)
      .required(STRINGS.YUP.EMAIL_IS_REQUIRED),
    ...(captchaEnabled && {
      recaptcha: yup.boolean().oneOf([true], STRINGS.YUP.RE_CAPTCHA_IS_REQUIRED)
    })
  });

  const getDefaultValues = () => ({
    email: '',
    recaptcha: false
  });

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    defaultValues: getDefaultValues(),
    resolver: yupResolver(ForgotPasswordSchema)
  });

  useEffect(() => {
    return () => {
      dispatch(resetPasswordStoreValues());
    };
  }, [dispatch]);

  const onSubmit = (data: any) => {
    dispatch(emailVerify({ email: data.email }));
  };

  return (
    <div
      className="d-flex flex-column flex-grow-1"
      data-testid="forgotPasswordPage"
    >
      {emailVerified ? (
        <FormContainer>
          <h1>{STRINGS.CHECK_YOUR_EMAIL}</h1>
          <p className="text-description">{message}</p>
          <div className="my-5 d-flex justify-content-center">
            <Icon
              icon="mail_01"
              stroke={`${colorState.icon.brand.primary}`}
              width={90}
              height={74}
            />
          </div>

          <Button
            className={'w-100'}
            data-testid="goToLoginPageButton"
            onClick={props.goBack}
            {...btnStyles.primary}
          >
            <span className="me-1">{STRINGS.GO_TO_LOGIN}</span>
            <Icon icon="arrow_narrow_right" stroke="#ffffff" />
          </Button>
        </FormContainer>
      ) : (
        <FormContainer>
          <h1>{STRINGS.FORGOT_PASSWORD}</h1>
          <p className="text-description">{STRINGS.PASSWORD_RESET_TEXT}</p>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-5 mt-3">
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="email"
                    data-testid="forgotPasswordEmailInput"
                    aria-label="Forgot Password Email Input"
                    inputSize="lg"
                    label="Email"
                    autocomplete="false"
                    asterisk
                    aria-required="true"
                    error={errors?.email}
                    hintText={errors?.email?.message}
                    placeholder={STRINGS.ENTER_EMAIL}
                    {...inputStyles}
                  />
                )}
              />
              {!emailVerified && message && (
                <ErrorMessage>{`${message}`}</ErrorMessage>
              )}
              {captchaEnabled && (
                <>
                  <div className="form-group">
                    <ErrorBoundary
                      fallback={
                        <div style={{ color: 'red' }}>Issue with ReCaptcha</div>
                      }
                    >
                      <Controller
                        name={'recaptcha'}
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange } }) => (
                          <ReCAPTCHA
                            sitekey={REACPTCHA_SITE_KEY}
                            onChange={(e) => {
                              if (e) {
                                onChange(e);
                                setValue('recaptcha', true);
                                clearErrors('recaptcha');
                              } else {
                                setValue('recaptcha', false);
                              }
                            }}
                            aria-label="recaptcha"
                          />
                        )}
                      />
                    </ErrorBoundary>
                  </div>
                  {errors?.recaptcha && (
                    <ErrorMessage>{STRINGS.RECAPTCHA_REQUIRED}.</ErrorMessage>
                  )}
                </>
              )}
              {errorCode === 429 && (
                <ErrorMessage>{STRINGS.PASSWORD_REQUESTS_LIMITED}</ErrorMessage>
              )}
            </div>
            <Button
              data-testid="submitForgotPasswordButton"
              className={'w-100'}
              type="submit"
              {...btnStyles.primary}
            >
              {STRINGS.SUBMIT}
            </Button>
          </form>
          <FlexCenter className="flex-column mt-4">
            <Button
              onClick={props.goBack}
              data-testid="goBackToLoginPageButton"
              {...btnStyles.tertiary}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  props.goBack();
                }
              }}
              className="w-100"
            >
              <Icon
                icon="arrow_narrow_left"
                stroke={`${colorState.icon.brand.primary}`}
              />
              <span className="ms-1">{STRINGS.GO_BACK_TO_LOGIN_PAGE}</span>
            </Button>
          </FlexCenter>
        </FormContainer>
      )}
    </div>
  );
}
